import React, {
  useCallback,
  useContext,
  useReducer,
  useRef,
  useState
} from 'react';
import { FetchCallbacksContext } from '../../FetchCallbacks';
import FormBuilderCollaborateWizardView from '../view/FormBuilderCollaborateWizardView';
import generateId from '../../../utils/generateId';
import useFetchWithCallbacks from '../../../hooks/useFetchWithCallbacks';

const WizardReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case 'update':
      return {
        ...state,
        collaboratorData: { ...state.collaboratorData, ...payload }
      };
    default:
      return state;
  }
};

const generateAdminId = () => 'admin_' + generateId();

const getUserLabel = u => {
  let alreadyAdmin = false;
  if (u.rolesData && u.rolesData.role !== 'end_user') {
    alreadyAdmin = true;
  }

  return {
    text: u.info.firstName + ' ' + u.info.lastName,
    value: {
      label: u.info.firstName + ' ' + u.info.lastName,
      value: u.id,
      data: {
        firstName: u.info.firstName,
        lastName: u.info.lastName,
        email: u.info.email,
        schema: u.schema,
        _id: generateAdminId(),
        alreadyAdmin: alreadyAdmin
      }
    }
  };
};

const FormBuilderCollaborateWizardEngine = ({
  allGroups = [],
  collaborators = [],
  defaultAssignee,
  form,
  formId,
  interpretCollaboratorsAndSave,
  loadCollaborators = () => {},
  onWizardClose = () => {},
  collaboratorData = {},
  editMode = false,
  setWarningDialogOpen: setParentWarningDialogOpen
}) => {
  const fetchAPI = useFetchWithCallbacks();
  const { openSnackbar } = useContext(FetchCallbacksContext);
  const [data, dispatch] = useReducer(WizardReducer, {
    collaboratorData
  });

  const initialQuery =
    collaboratorData && collaboratorData.query ? collaboratorData.query : [];

  const [query, setQuery] = useState(initialQuery);

  const [showingCollaboratorValue, setShowingCollaboratorValue] =
    useState(collaboratorData);
  const [submitting, setSubmitting] = useState(false);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState(
    collaboratorData && collaboratorData.groups ? collaboratorData.groups : []
  );
  const [notificationType, setNotificationType] = useState(
    collaboratorData && collaboratorData.notificationInfo
      ? collaboratorData.notificationInfo
      : 'ALWAYS'
  );
  const [viewOnly, setViewOnly] = useState(collaboratorData?.viewOnly || false);
  const changedDropdown = val => {
    let flattenedVal;
    if (!val) {
      return;
    }
    flattenedVal = {
      ...val.data,
      label: '' + val.data.firstName + ' ' + val.data.lastName,
      value: val.value
    };
    setShowingCollaboratorValue(flattenedVal);
  };

  const openMenuOptionParser = opt => {
    const userTypeLabel =
      opt.value.data.schema === 'external' ? 'External user' : 'Internal user';
    return (
      <p style={{ margin: 0 }}>
        {opt.value.data.firstName + ' ' + opt.value.data.lastName}
        <br />
        <small>
          {opt.value.data.email}
          {opt.value.data.schema && form.allowExternalUserAccounts ? (
            <>
              &nbsp;&nbsp;&nbsp;◀●▶&nbsp;&nbsp;&nbsp;
              {userTypeLabel}
            </>
          ) : (
            ''
          )}
        </small>
      </p>
    );
  };

  const changedNotificationDropdown = val => {
    setNotificationType(val);
  };

  const dirty = useRef(false);
  const [isDiscardChangesDialogOpen, setIsDiscardChangesDialogOpen] =
    useState(false);

  const onCancel = useCallback(() => {
    if (!dirty.current) {
      onWizardClose();
      return;
    }
    setIsDiscardChangesDialogOpen(true);
  }, [onWizardClose]);

  const onSave = useCallback(() => {
    let matchingCollaborators,
      onePromise,
      listOfDistinctCollaborators,
      promises,
      savingUserId;
    setSubmitting(true);
    promises = [];
    savingUserId = showingCollaboratorValue.value;
    listOfDistinctCollaborators =
      collaborators.flatMap(col => {
        return {
          user_id: col.value,
          groups: col.groups,
          notificationInfo: col.notificationInfo,
          alreadyAdmin: col.alreadyAdmin,
          query: col.query,
          bucketId: col.bucketId,
          viewOnly: col.viewOnly
        };
      }) || [];
    matchingCollaborators = listOfDistinctCollaborators.filter(
      set => set.user_id === savingUserId
    );
    if (matchingCollaborators.length) {
      matchingCollaborators[0].groups = selectedGroups;
      matchingCollaborators[0].notificationInfo = notificationType;
      if (query) {
        matchingCollaborators[0].query = query;
      }
      matchingCollaborators[0].viewOnly = viewOnly;
    } else {
      let newSet = {
        user_id: showingCollaboratorValue.value,
        groups: selectedGroups,
        notificationInfo: notificationType,
        alreadyAdmin: showingCollaboratorValue.alreadyAdmin,
        viewOnly: viewOnly
      };
      if (query) {
        newSet.query = query;
      }
      listOfDistinctCollaborators.push(newSet);
      if (!showingCollaboratorValue.alreadyAdmin) {
        onePromise = new Promise((resolve, reject) => {
          fetchAPI('/auth/v1/userroles', {
            method: 'POST',
            body: {
              role_code: 'form_admin',
              user_id: showingCollaboratorValue.value,
              filterIds: []
            }
          })
            .then(response => {
              resolve();
            })
            .catch(e => {
              reject();
            });
        });
        promises.push(onePromise);
      }
    }
    Promise.all(promises)
      .then(() => {
        interpretCollaboratorsAndSave(listOfDistinctCollaborators);
      })
      .catch(e => {
        openSnackbar('error', 'snackbars.assignees.failure');
      });
  }, [
    collaborators,
    fetchAPI,
    interpretCollaboratorsAndSave,
    selectedGroups,
    showingCollaboratorValue,
    notificationType,
    openSnackbar,
    query,
    viewOnly
  ]);

  const onDelete = () => {
    setParentWarningDialogOpen({
      action: 'delete',
      which: collaboratorData._id
    });
  };

  const onViewOnlyChanged = e => {
    setViewOnly(e.target.value);
  };

  const loadOptions = v => {
    return fetchAPI(
      `/users/v1/users?offset=0&role=admin&limit=100&sort='info'->'firstName' asc&query=[
        [{"field":"'status'","op":"=","value":"'ACTIVE'"}],
        [
          {"field": "'info'->'email'", "op": "ILIKE", "value": "%${v}%", "internOp": "OR"},
          {"field": "'info'->'lastName'", "op": "ILIKE", "value": "%${v}%", "internOp": "OR"},
          {"field": "'info'->'userName'", "op": "ILIKE", "value": "%${v}%", "internOp": "OR"},
          {"field": "'info'->'firstName'", "op": "ILIKE", "value": "%${v}%", "internOp": "OR"}
      ],
      [{"field":"'schema'", "op": "=", "value": "'internal'"}]
    ]`.replace(/\n\s+/g, '    ')
    ).then(({ results }) => {
      return results
        .filter(u => {
          var alreadyInList = collaborators.filter(c => c.value === u.id);
          if (alreadyInList.length) return false;
          return u;
        })
        .map(getUserLabel);
    });
  };

  const isSaveBtnDisabled = useCallback(() => {
    const isUserSelected =
      showingCollaboratorValue && showingCollaboratorValue.value ? true : false;
    if (editMode) {
      if (submitting) {
        return true;
      }
      return false;
    }
    return !isUserSelected || submitting;
  }, [editMode, showingCollaboratorValue, submitting]);

  const changedGroupSelection = event => {
    let groups = event.target.value;
    if (event.currentTarget.getAttribute('data-value') === 'all') {
      groups = ['all'];
    } else {
      if (groups.indexOf('all') !== -1) {
        groups = groups.filter(v => v !== 'all');
      }
      if (
        showingCollaboratorValue.value === defaultAssignee &&
        JSON.stringify(showingCollaboratorValue.groups || []) ===
          JSON.stringify(selectedGroups)
      ) {
        setWarningDialogOpen({
          action: 'group',
          groups: groups
        });
        return;
      }
    }
    setSelectedGroups(groups);
  };

  const confirmChangingGroup = useCallback(() => {
    let groups;
    if (!warningDialogOpen || !warningDialogOpen.groups) {
      return;
    }
    groups = warningDialogOpen.groups;
    setSelectedGroups(groups);
    setWarningDialogOpen(false);
  }, [warningDialogOpen]);

  const updateQuery = q => {
    setQuery(q);
  };

  const viewProps = {
    allGroups,
    changedDropdown,
    changedGroupSelection,
    changedNotificationDropdown,
    confirmChangingGroup,
    data,
    dispatch,
    editMode,
    selectedGroups,
    isDiscardChangesDialogOpen,
    isSaveBtnDisabled,
    loadOptions,
    notificationType,
    onDelete,
    onCancel,
    onSave,
    onViewOnlyChanged,
    openMenuOptionParser,
    query,
    setParentWarningDialogOpen,
    setWarningDialogOpen,
    showingCollaboratorValue,
    submitting,
    updateQuery,
    viewOnly,
    warningDialogOpen
  };

  return <FormBuilderCollaborateWizardView {...viewProps} />;
};

export default FormBuilderCollaborateWizardEngine;
